// Transitions PR-5 — the guided start wizard for all seven exit paths.
// Replaces PR-1's plain typed start form (StartTransitionModal): the
// RecordPaymentWizard shell (Modal size="lg" + StatusStepper + footer
// Cancel/Back/Next) with per-family steps, smart defaults from the loan's
// real data, and the load-bearing consequence copy at every decision. The
// state-machine rules render visually: the payoff family's irrevocability
// warning sits on the notice step, the extension wizard refuses outside the
// Month-73 window and at extension #3, and the resale/right-size paths hand
// off to their dedicated engine wizards (their forms are never duplicated).

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircleQuestion } from "lucide-react";
import { Button, Input, Label, StatusBadge, StatusStepper, Textarea } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency, operationalToday } from "@/design-system/lib/format";
import { calendarDaysUntil } from "@/utils/datetime";
import {
  TRANSITION_TYPE_INFO,
  type LoanTransitionDto,
  type LoanTransitionType,
} from "@/api/portfolioAdmin/loanTransitionsApi";
import {
  useBmspCountdown,
  useChangeLoanTransitionStatus,
  useCreateLoanTransition,
  useLinkableDeals,
  useLoanTransitions,
  usePayoffQuotePreview,
  useRecordPrepaymentNotice,
} from "@/hooks/portfolioAdmin/useLoanTransitions";
import type { LoanSummaryDto } from "@/api/portfolioAdmin/types";
import { GuarantyFactsPanel } from "./GuarantyFactsPanel";
import { TransitionExplainModal } from "./TransitionExplainModal";
import {
  ASSUMPTION_FEE_FACT,
  ENGINE_HANDOFF,
  EXTENSION_FEE_FACT,
  EXTENSION_MAX_TWO,
  EXTENSION_NO_REPRICE,
  FAMILY_STEPS,
  HARDSHIP_FRAMING,
  IRREVOCABILITY_WARNING,
  PATH_FACTS,
  PATH_GROUPS,
  PERIMETER_CONSEQUENCES,
  YMP_FRAMING,
  familyFor,
} from "./transitionsWizardCopy";

function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  const d = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

/**
 * 🔴 THIS ONE GATES A BUTTON, so its old off-by-one refused valid input rather
 * than merely mislabelling it. It was already midnight-anchored — the closest
 * of the rivals — but read TODAY in UTC (`new Date().toISOString().slice(0,10)`).
 * After 5pm in Phoenix that is tomorrow's date, so `daysUntil(today) === -1`
 * and `tenderDateValid` refused the operator the current day, on a form whose
 * own error text says "on or after today". Evenings only, which is why it
 * survived: at 9am it behaves perfectly.
 *
 * 🔴 THE `min=` ON THE INPUT THIS GATE GUARDS WAS DELIBERATELY LEFT BEHIND, and
 * it is worth knowing why rather than assuming an oversight. For one release the
 * gate accepted the current Phoenix day while `min` still named the UTC day, so
 * the form accepted a day its own picker would not offer. That was the LESSER of
 * two evils and was filed as such in `todaySeeds.test.ts`'s
 * BOUNDS_AWAITING_SERVER_SIDE: the SERVER still read the UTC day too, so moving
 * `min` alone would have had the client offer a date the server then refused —
 * trading an inconsistency the operator can work around for one they cannot.
 *
 * The named collector landed (`LoanTransitionService` now reads
 * `OperationalDay.Today()`), so both halves read `operationalToday()` and the
 * carve-out entry is deleted. The tender-date tests pin the gate and the floor
 * naming the SAME day, so neither can drift back alone.
 *
 * Collapses to a number so the `>= 0` gate below stays a plain comparison. The
 * fallback is -1, NOT 0: an unparseable value must fail CLOSED, which is what
 * the old code did by accident — it produced `NaN`, and `NaN >= 0` is false.
 * Returning 0 would read as "today" and let garbage through a gate on an
 * irrevocable notice. Unreachable via `<input type="date">` (which yields "" or
 * a valid day, and the caller has already excluded ""), but a gate's default
 * belongs on the refusing side regardless.
 */
function daysUntil(isoDate: string): number {
  return calendarDaysUntil(isoDate) ?? -1;
}

// A sane far-future upper bound so an absurd 6-digit year can't be entered.
// Computed once at module load (not per render) to keep pure code deterministic.
//
// Derived from the operational day like the floor below it. The old shape read
// the HOST's local year (`new Date().getFullYear()`) and then re-encoded it
// through `toISOString()`, so east of UTC it landed a day early — immaterial at
// a 30-year bound, and not worth leaving as the one bound in the file computed
// on a different clock from every other.
const TENDER_DATE_MAX = `${Number(operationalToday().slice(0, 4)) + 30}-01-01`;

export function TransitionStartWizard({
  loan,
  open,
  onClose,
}: {
  loan: LoanSummaryDto;
  open: boolean;
  onClose: () => void;
}) {
  const [type, setType] = useState<LoanTransitionType | null>(null);
  const [step, setStep] = useState(0);
  const [recordNoticeNow, setRecordNoticeNow] = useState(false);
  const [tenderDate, setTenderDate] = useState("");
  const [noticeAck, setNoticeAck] = useState(false);
  const [linkedDealId, setLinkedDealId] = useState("");
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<LoanTransitionDto | null>(null);
  const [askAiOpen, setAskAiOpen] = useState(false);

  // Fresh run per open.
  useEffect(() => {
    if (open) {
      setType(null);
      setStep(0);
      setRecordNoticeNow(false);
      setTenderDate("");
      setNoticeAck(false);
      setLinkedDealId("");
      setNote("");
      setError(null);
      setResult(null);
      setAskAiOpen(false);
    }
  }, [open]);

  const family = type ? familyFor(type) : null;
  const steps = family
    ? FAMILY_STEPS[family]
    : [FAMILY_STEPS.payoff[0], { key: "review", label: "Review & start" }];
  const stepKey = steps[Math.min(step, steps.length - 1)].key;
  const isLast = step === steps.length - 1;

  const transitionsQuery = useLoanTransitions(loan.id);
  const bmsp = useBmspCountdown(loan.id);
  const quote = usePayoffQuotePreview(
    loan.id,
    undefined,
    open && family === "payoff" && step >= 1,
  );
  const linkableDeals = useLinkableDeals(open && family === "assumption" && stepKey === "deal");

  const createMutation = useCreateLoanTransition(loan.id);
  const noticeMutation = useRecordPrepaymentNotice(loan.id);
  const statusMutation = useChangeLoanTransitionStatus(loan.id);
  const submitting =
    createMutation.isPending || noticeMutation.isPending || statusMutation.isPending;

  // Smart context: is the BMSP application window open right now?
  const countdown = bmsp.data ?? null;
  const bmspWindowOpen =
    countdown != null &&
    countdown.hasBmspWindow &&
    !countdown.windowMissed &&
    (countdown.daysToWindowOpen == null || countdown.daysToWindowOpen <= 0) &&
    (countdown.daysToDeadline == null || countdown.daysToDeadline >= 0);

  // The at-most-two rule, mirrored visually from the loan's own history.
  const completedExtensions = (transitionsQuery.data ?? []).filter(
    (t) => t.type === "BmspMaturityExtension" && t.status === "Completed",
  ).length;

  const extensionBlockers: string[] = [];
  if (family === "extension") {
    if (countdown == null || !countdown.hasBmspWindow) {
      extensionBlockers.push(
        "This loan has no BMSP application window (not a live balloon loan, or the window cannot be derived).",
      );
    } else {
      if (countdown.daysToWindowOpen != null && countdown.daysToWindowOpen > 0) {
        extensionBlockers.push(
          `The application window has not opened yet — it opens ${fmtDate(countdown.windowOpen)} (in ${countdown.daysToWindowOpen} days).`,
        );
      }
      if (countdown.windowMissed) {
        extensionBlockers.push(
          `The application window closed ${fmtDate(countdown.applicationDeadline)} — the Month-73 deadline was missed.`,
        );
      }
    }
    if (completedExtensions >= 2) {
      extensionBlockers.push(
        "This loan has already used both extensions — the BLA allows at most two.",
      );
    }
  }

  const tenderDateValid = tenderDate !== "" && daysUntil(tenderDate) >= 0;
  const tenderDateInvalid = tenderDate !== "" && !tenderDateValid;
  const stepValid = (() => {
    switch (stepKey) {
      case "path":
        return type != null;
      case "quote":
        return !quote.isLoading;
      case "notice":
        return !recordNoticeNow || (tenderDateValid && noticeAck);
      case "window":
        return extensionBlockers.length === 0 && !bmsp.isLoading && !transitionsQuery.isLoading;
      default:
        return true;
    }
  })();

  const submit = async () => {
    if (!type) return;
    setError(null);
    try {
      const created = await createMutation.mutateAsync({
        type,
        initiate: true,
        note: note.trim() || null,
      });
      if (family === "payoff" && recordNoticeNow && tenderDateValid) {
        await noticeMutation.mutateAsync({
          transitionId: created.id,
          body: { tenderDate, note: null },
        });
      }
      if (family === "assumption" && linkedDealId) {
        await statusMutation.mutateAsync({
          transitionId: created.id,
          body: { targetStatus: "AwaitingLinkedDeal", linkedDealId },
        });
      }
      setResult(created);
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  };

  const handleNext = () => {
    if (isLast) void submit();
    else setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  const feeOnBalance = (pct: number) => formatCurrency(loan.currentBalance * pct);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Start a transition"
      description="A guided path off the loan — pick the exit, see the consequences, then start it."
      size="lg"
      dismissOnBackdropClick={false}
      footer={
        result ? (
          <div className="flex items-center justify-end gap-space-2">
            {result.type === "PhoenixBackedResale" || result.type === "RightSizeRefinance" ? (
              <Link
                to={ENGINE_HANDOFF[result.type].route(loan.id)}
                className="inline-flex"
                data-testid="engine-wizard-link"
              >
                <Button type="button" variant="secondary">
                  {ENGINE_HANDOFF[result.type].cta}
                </Button>
              </Link>
            ) : null}
            <Button type="button" onClick={onClose} data-testid="wizard-done">
              Done
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-space-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={submitting}
              data-testid="wizard-cancel"
            >
              Cancel
            </Button>
            {step > 0 && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep((s) => Math.max(s - 1, 0))}
                disabled={submitting}
                data-testid="wizard-back"
              >
                Back
              </Button>
            )}
            <Button
              type="button"
              onClick={handleNext}
              disabled={!stepValid || submitting}
              data-testid="wizard-next"
            >
              {submitting ? "Starting…" : isLast ? "Start transition" : "Next"}
            </Button>
          </div>
        )
      }
    >
      {result ? (
        <div className="flex flex-col gap-space-3" data-testid="wizard-success">
          <p className="text-body m-0">
            <span className="font-semibold">{TRANSITION_TYPE_INFO[result.type].label}</span>{" "}
            transition started.
            {result.tenderDate &&
              ` The irrevocable Prepayment Notice is recorded — tender by ${fmtDate(result.tenderDate)}.`}
          </p>
          {(result.type === "PhoenixBackedResale" || result.type === "RightSizeRefinance") && (
            <p className="text-small text-gray-mid m-0">
              {ENGINE_HANDOFF[result.type].body}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-space-4" data-testid="transition-start-wizard">
          <div className="flex items-start justify-between gap-space-2">
            <StatusStepper
              steps={steps}
              currentIndex={step}
              label="Transition wizard steps"
              data-testid="transition-wizard-stepper"
            />
            <Button
              type="button"
              variant="text"
              size="sm"
              onClick={() => setAskAiOpen(true)}
              data-testid="ask-ai-button"
            >
              <MessageCircleQuestion size={14} aria-hidden /> Ask AI
            </Button>
          </div>

          {/* ---- Step: pick the exit path ---- */}
          {stepKey === "path" && (
            <div className="flex flex-col gap-space-3">
              {bmspWindowOpen && (
                <div
                  className="rounded border border-border-light bg-white-warm p-space-3"
                  data-testid="bmsp-suggestion"
                >
                  <p className="text-small m-0">
                    <span className="font-semibold">This balloon loan's BMSP window is open</span>{" "}
                    — the application deadline is {fmtDate(countdown!.applicationDeadline)}. The
                    balloon-maturity paths below are the intended exits for this window.
                  </p>
                </div>
              )}
              {PATH_GROUPS.map((group) => (
                <div key={group.label}>
                  <p className="text-caption uppercase tracking-wide text-gray-mid m-0 mb-space-1">
                    {group.label}
                  </p>
                  <div className="flex flex-col gap-space-1" role="radiogroup" aria-label={group.label}>
                    {group.types.map((t) => (
                      <label
                        key={t}
                        className={
                          "flex items-start gap-space-2 rounded border p-space-2 cursor-pointer " +
                          (type === t ? "border-gold bg-white-warm" : "border-border-light")
                        }
                      >
                        <input
                          type="radio"
                          name="transition-path"
                          checked={type === t}
                          onChange={() => setType(t)}
                          data-testid={`path-option-${t}`}
                        />
                        <span>
                          <span className="text-small font-semibold block">
                            {TRANSITION_TYPE_INFO[t].label}
                            {t === "BmspMaturityExtension" && bmspWindowOpen && (
                              <StatusBadge tone="ok" className="ml-space-2">
                                Window open
                              </StatusBadge>
                            )}
                          </span>
                          <span className="text-small text-gray-mid block">
                            {TRANSITION_TYPE_INFO[t].description}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ---- Payoff family: the itemized quote ---- */}
          {stepKey === "quote" && (
            <div className="flex flex-col gap-space-3">
              {(type === "BmspFullPayoff" || type === "BmspThirdPartyRefinance") && countdown && (
                <p className="text-small text-gray-mid m-0" data-testid="balloon-context-line">
                  Balloon context: month {countdown.currentMonthNumber} of {countdown.termMonths};
                  the balloon matures {fmtDate(countdown.balloonMaturity)} ({countdown.daysToMaturity}{" "}
                  days away).
                </p>
              )}
              {quote.isLoading || !quote.data ? (
                <p className="text-small text-gray-mid m-0">Computing today's quote…</p>
              ) : (
                <table className="w-full text-small" data-testid="wizard-quote-table">
                  <tbody>
                    {(
                      [
                        ["Outstanding principal", quote.data.outstandingPrincipal],
                        ["Accrued interest", quote.data.accruedInterest],
                        ["Open arrears", quote.data.openArrears],
                        [
                          `Prepayment administration fee (${(quote.data.prepaymentAdminFeePct * 100).toFixed(2)}%)`,
                          quote.data.prepaymentAdminFee,
                        ],
                        ["Yield-maintenance premium", quote.data.yieldMaintenancePremium],
                      ] as const
                    ).map(([label, amount]) => (
                      <tr key={label} className="border-b border-border-light">
                        <td className="py-space-1">{label}</td>
                        <td className="py-space-1 text-right">{formatCurrency(amount)}</td>
                      </tr>
                    ))}
                    <tr>
                      <td className="py-space-1 font-semibold">Total payoff today</td>
                      <td className="py-space-1 text-right font-semibold" data-testid="quote-total">
                        {formatCurrency(quote.data.totalPayoff)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              )}
              <p className="text-small text-gray-mid m-0" data-testid="ymp-framing">
                {YMP_FRAMING} A quote is informational — nothing is committed until the Prepayment
                Notice is recorded.
              </p>
            </div>
          )}

          {/* ---- Payoff family: the irrevocable notice (the load-bearing moment) ---- */}
          {stepKey === "notice" && (
            <div className="flex flex-col gap-space-3">
              <div
                className="rounded border border-status-error p-space-3"
                data-testid="irrevocability-warning"
              >
                <p className="text-small font-semibold text-status-error m-0 mb-space-1">
                  The Prepayment Notice is irrevocable
                </p>
                <p className="text-small m-0">{IRREVOCABILITY_WARNING}</p>
              </div>
              <div className="flex flex-col gap-space-1" role="radiogroup" aria-label="Notice timing">
                <label className="flex items-center gap-space-2 text-small">
                  <input
                    type="radio"
                    name="notice-timing"
                    checked={!recordNoticeNow}
                    onChange={() => setRecordNoticeNow(false)}
                    data-testid="notice-later"
                  />
                  Start the transition without the notice — record it when the buyer commits in
                  writing.
                </label>
                <label className="flex items-center gap-space-2 text-small">
                  <input
                    type="radio"
                    name="notice-timing"
                    checked={recordNoticeNow}
                    onChange={() => setRecordNoticeNow(true)}
                    data-testid="notice-now"
                  />
                  The buyer has committed — record the irrevocable notice now.
                </label>
              </div>
              {recordNoticeNow && (
                <div className="flex flex-col gap-space-2">
                  <div>
                    <Label htmlFor="wizard-tender-date">Committed tender date</Label>
                    <Input
                      id="wizard-tender-date"
                      type="date"
                      min={operationalToday()}
                      max={TENDER_DATE_MAX}
                      invalid={tenderDateInvalid}
                      value={tenderDate}
                      onChange={(e) => setTenderDate(e.target.value)}
                      data-testid="wizard-tender-date"
                    />
                    {tenderDateInvalid && (
                      <p
                        className="text-small text-status-error m-0 mt-space-1"
                        role="alert"
                        data-testid="tender-date-error"
                      >
                        Enter a valid tender date on or after today.
                      </p>
                    )}
                    {tenderDateValid && (
                      <p className="text-small text-gray-mid m-0 mt-space-1" data-testid="tender-preview">
                        {daysUntil(tenderDate)} day{daysUntil(tenderDate) === 1 ? "" : "s"} until
                        tender — a countdown will run on the transition until then.
                      </p>
                    )}
                  </div>
                  <label className="flex items-start gap-space-2 text-small">
                    <input
                      type="checkbox"
                      checked={noticeAck}
                      onChange={(e) => setNoticeAck(e.target.checked)}
                      data-testid="notice-ack"
                    />
                    <span>
                      I understand this notice cannot be canceled and a missed tender is an
                      automatic event of default.
                    </span>
                  </label>
                </div>
              )}
            </div>
          )}

          {/* ---- Extension: the eligibility window ---- */}
          {stepKey === "window" && (
            <div className="flex flex-col gap-space-3" data-testid="extension-window-step">
              {countdown ? (
                <div className="text-small flex flex-col gap-space-1">
                  <p className="m-0">
                    This loan is in month{" "}
                    <span className="font-semibold">{countdown.currentMonthNumber}</span> of{" "}
                    {countdown.termMonths}; the balloon matures{" "}
                    {fmtDate(countdown.balloonMaturity)}.
                  </p>
                  <p className="m-0">
                    BMSP application window: {fmtDate(countdown.windowOpen)} →{" "}
                    {fmtDate(countdown.applicationDeadline)}.
                  </p>
                  <p className="m-0 text-gray-mid">
                    Extensions used: {completedExtensions} of 2.
                  </p>
                </div>
              ) : (
                <p className="text-small text-gray-mid m-0">Loading the covenant countdown…</p>
              )}
              {extensionBlockers.length === 0 ? (
                countdown && (
                  <div
                    className="rounded border border-border-light bg-white-warm p-space-3"
                    data-testid="extension-eligible"
                  >
                    <p className="text-small m-0 font-semibold">
                      The application window is open — this loan can apply for an extension.
                    </p>
                  </div>
                )
              ) : (
                <div
                  className="rounded border border-status-error p-space-3"
                  data-testid="extension-blocked"
                >
                  <p className="text-small font-semibold text-status-error m-0 mb-space-1">
                    An extension cannot be started
                  </p>
                  <ul className="m-0 pl-space-4">
                    {extensionBlockers.map((b) => (
                      <li key={b} className="text-small">
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ---- Extension: fee & terms ---- */}
          {stepKey === "terms" && (
            <div className="flex flex-col gap-space-3" data-testid="extension-terms-step">
              <div className="rounded border border-border-light bg-white-warm p-space-3">
                <p className="text-small font-semibold m-0" data-testid="no-reprice-corrective">
                  {EXTENSION_NO_REPRICE}
                </p>
              </div>
              <ul className="m-0 pl-space-4 flex flex-col gap-space-1">
                <li className="text-small" data-testid="extension-fee-line">
                  {EXTENSION_FEE_FACT} On today's balance that is about{" "}
                  <span className="font-semibold">{feeOnBalance(0.01)}</span>.
                </li>
                <li className="text-small">
                  The extension term (1–5 years) is chosen when the extension executes, after
                  note-holder approval.
                </li>
                <li className="text-small">{EXTENSION_MAX_TWO}</li>
                <li className="text-small">
                  Monthly payments continue unchanged until the new balloon maturity.
                </li>
              </ul>
            </div>
          )}

          {/* ---- Assumption family: the incoming buyer's deal ---- */}
          {stepKey === "deal" && (
            <div className="flex flex-col gap-space-3">
              <p className="text-small text-gray-mid m-0">
                An assumption usually waits on the incoming buyer's deal. Link it now if it exists —
                the transition parks in "Awaiting linked deal" until that deal closes — or link it
                later from the transition card.
              </p>
              <div>
                <Label htmlFor="wizard-linked-deal">Incoming deal (optional)</Label>
                <select
                  id="wizard-linked-deal"
                  className="w-full rounded border border-border-light bg-white-pure px-space-2 py-space-1 text-small"
                  value={linkedDealId}
                  onChange={(e) => setLinkedDealId(e.target.value)}
                  data-testid="wizard-linked-deal"
                >
                  <option value="">
                    {linkableDeals.isLoading ? "Loading deals…" : "Link later"}
                  </option>
                  {(linkableDeals.data ?? []).map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.dealNumber}
                      {d.businessName ? ` — ${d.businessName}` : ""} ({d.stage})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* ---- Assumption family: what changes hands ---- */}
          {stepKey === "changes" && (
            <div className="flex flex-col gap-space-3" data-testid="assumption-changes-step">
              {type === "HardshipExit" && (
                <div className="rounded border border-border-light bg-white-warm p-space-3">
                  <p className="text-small m-0" data-testid="hardship-framing">
                    {HARDSHIP_FRAMING}
                  </p>
                </div>
              )}
              <div>
                <p className="text-small font-semibold m-0 mb-space-1">
                  When the assumption executes:
                </p>
                <ul
                  className="m-0 pl-space-4 flex flex-col gap-space-1"
                  data-testid="perimeter-consequences"
                >
                  {PERIMETER_CONSEQUENCES.map((c) => (
                    <li key={c} className="text-small">
                      {c}
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-small m-0" data-testid="assumption-fee-line">
                {ASSUMPTION_FEE_FACT} On today's balance that is about{" "}
                <span className="font-semibold">{feeOnBalance(0.03)}</span> (
                {feeOnBalance(0.01)} processing + {feeOnBalance(0.02)} assumption).
              </p>
            </div>
          )}

          {/* ---- Assumption family: guaranty truth-from-data ---- */}
          {stepKey === "guaranties" && <GuarantyFactsPanel loanId={loan.id} />}

          {/* ---- Engine paths: the handoff ---- */}
          {stepKey === "handoff" &&
            (type === "PhoenixBackedResale" || type === "RightSizeRefinance") && (
              <div className="flex flex-col gap-space-2" data-testid="engine-handoff-step">
                <p className="text-small font-semibold m-0">{ENGINE_HANDOFF[type].title}</p>
                <p className="text-small m-0">{ENGINE_HANDOFF[type].body}</p>
              </div>
            )}

          {/* ---- Review & start ---- */}
          {stepKey === "review" && type && (
            <div className="flex flex-col gap-space-3" data-testid="wizard-review-step">
              <div className="rounded border border-border-light bg-white-warm p-space-3">
                <p className="text-small font-semibold m-0 mb-space-1">
                  {TRANSITION_TYPE_INFO[type].label} — {loan.loanNumber}
                </p>
                <p className="text-small m-0">{PATH_FACTS[type]}</p>
              </div>
              <ul className="m-0 pl-space-4 flex flex-col gap-space-1 text-small">
                {family === "payoff" && quote.data && (
                  <li>Today's payoff quote: {formatCurrency(quote.data.totalPayoff)}.</li>
                )}
                {family === "payoff" && (
                  <li data-testid="review-notice-line">
                    {recordNoticeNow && tenderDateValid
                      ? `The irrevocable Prepayment Notice will be recorded with tender due ${fmtDate(tenderDate)}.`
                      : "No notice yet — record the irrevocable Prepayment Notice when the buyer commits."}
                  </li>
                )}
                {family === "extension" && (
                  <li>
                    Extension fee at execution: about {feeOnBalance(0.01)} (1% of outstanding
                    principal). The rate stays 10.00%.
                  </li>
                )}
                {family === "assumption" && (
                  <li data-testid="review-deal-line">
                    {linkedDealId
                      ? "The transition will park in Awaiting linked deal, tied to the selected deal."
                      : "No deal linked yet — the transition starts Initiated."}
                  </li>
                )}
                {family === "assumption" && (
                  <li>Assumption fee at execution: about {feeOnBalance(0.03)} (3% consolidated).</li>
                )}
                {family === "engine" && (
                  <li>
                    Execution runs in the dedicated{" "}
                    {type === "PhoenixBackedResale" ? "Resale" : "Right-Size"} wizard once this
                    transition is started.
                  </li>
                )}
              </ul>
              <div>
                <Label htmlFor="wizard-note">Note (optional)</Label>
                <Textarea
                  id="wizard-note"
                  rows={2}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-small text-status-error m-0" role="alert" data-testid="wizard-error">
              {error}
            </p>
          )}

          {askAiOpen && (
            <TransitionExplainModal
              loanId={loan.id}
              context={{
                pathType: type,
                pathFacts: type ? PATH_FACTS[type] : null,
                stepKey,
              }}
              onClose={() => setAskAiOpen(false)}
            />
          )}
        </div>
      )}
    </Modal>
  );
}
